/**
 * The past ticket a scenario came from: who wrote, how long the thread is,
 * when it was resolved, and the whole conversation one click away. Fetched by
 * ticket id through the thread API, so the scenario row itself stays a pointer.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTicketThread } from '@/hooks';
import { cn } from '@/lib/utils';
import { threadToTranscript } from '@/services/ticket.service';
import { PillButton } from '../ui/pill-button';
import { SourceCard } from '../ui/source-card';
import { MsgBubble } from './conversation-parts';
import { customerLabel, metaLine, useTicketTranscript, type TicketTranscript } from './ticket-transcript';

export const TicketMessages = ({ ticket }: { ticket: TicketTranscript }) => {
  if (ticket.isLoading) {
    return (
      <p className='flex items-center justify-center gap-2 p-8 text-xs text-gray-800'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading messages
      </p>
    );
  }
  if (ticket.isError || !ticket.thread) {
    return <p className='p-8 text-center text-xs text-gray-800'>The conversation could not be loaded.</p>;
  }
  const { externalTicketId } = ticket.thread;
  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4'>
      {ticket.transcript.map((m, i) => (
        <MsgBubble key={`${externalTicketId}-${i}`} m={m} />
      ))}
    </div>
  );
};

/**
 * The conversation dialog on its own, controlled by a ticket id: null keeps it closed. It
 * fetches through the same thread hook, so surfaces that only hold a ticket id (scenario
 * chips, bulk-run rows) open the viewer the simulator sidebar shows.
 */
export const TicketConversationDialog = ({
  ticketId,
  onOpenChange,
  className,
}: {
  ticketId: string | null;
  onOpenChange: (open: boolean) => void;
  /** Overrides the dialog's width, e.g. `sm:max-w-3xl`. */
  className?: string;
}) => {
  const ticket = useTicketTranscript(ticketId);

  return (
    <Dialog open={Boolean(ticketId)} onOpenChange={onOpenChange}>
      <DialogContent className={cn('flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl', className)}>
        <DialogHeader className='border-b border-gray-500 px-4 py-3'>
          <DialogTitle className='text-[15px]'>Ticket #{ticketId}</DialogTitle>
          <DialogDescription className='text-xs text-gray-800'>{ticket.summary ?? 'Loading'}</DialogDescription>
        </DialogHeader>
        <TicketMessages ticket={ticket} />
      </DialogContent>
    </Dialog>
  );
};

export const PastTicketCard = ({ ticketId }: { ticketId: string }) => {
  const { data: thread, isLoading, isError } = useTicketThread(ticketId);
  const [open, setOpen] = useState(false);

  if (isLoading) {
    return (
      <SourceCard
        title={`Ticket #${ticketId}`}
        meta={
          <span className='flex items-center gap-1.5'>
            <Loader2 className='h-3 w-3 animate-spin' />
            Loading
          </span>
        }
      />
    );
  }
  if (isError || !thread) return <SourceCard title={`Ticket #${ticketId}`} meta='Could not be loaded' />;

  return (
    <>
      <SourceCard
        title={customerLabel(thread) ?? `Ticket #${thread.externalTicketId}`}
        meta={`#${thread.externalTicketId} · ${metaLine(thread, threadToTranscript(thread).length)}`}
        action={
          <PillButton onClick={() => setOpen(true)} className='shrink-0'>
            View
          </PillButton>
        }
      />
      <TicketConversationDialog ticketId={open ? ticketId : null} onOpenChange={setOpen} />
    </>
  );
};
